'use client';

import { c as _c } from "react/compiler-runtime";
import { type GetLeaderboardRow, useAuth, useGetLeaderboard, usePrefetchGetUser } from '@morfi/backend-sdk-v2/client';
import { useTranslations } from 'next-intl';
import { useState } from 'react';
import { userIdentityKey } from '@/components/profile/user-identity-key';
import { parseAvatarPreferences } from '@/components/social/avatar-preferences';
import { LeaderboardEmptyState } from '@/components/social/leaderboard/leaderboard-empty';
import { LeaderboardSkeleton } from '@/components/social/leaderboard/leaderboard-skeleton';
import { TabContent } from '@/components/social/leaderboard/tab-content';
import { useAnchorScroll } from '@/components/social/leaderboard/use-anchor-scroll';
import { mapLeaderboardEntries, readFiniteNumber, readFiniteNumericString } from '@/components/social/leaderboard/use-committed-leaderboard-entries';
import { SocialFeedList } from '@/components/social/shared/social-feed-list';
import type { BackerEntry, BackerSort } from '@/components/social/types';
import { DataTable } from '@/components/ui/data-table';
import { Panel } from '@/components/ui/panel';
import { D6_SCALE } from '@/config/solana';
import { useLiveListData } from '@/hooks/sdk/use-live-list-data';
import { useFormattedCurrency } from '@/lib/currency';
import { useAppRouter } from '@/lib/network/use-app-router';
import { LB_BACKERS_FIELDS, PORTFOLIO_USER_PROFILE_FIELDS } from '@/lib/sdk/projections';
import { SOCIAL_LEADERBOARD_LIST_PANEL_CLASS } from '@/lib/ui/layout';

// ---------------------------------------------------------------------------
// Backers tab — consumes the universal DataTable primitive (spec 093).
// Three sortable columns: Backed amount (default), Backs, and Top Market (the
// backer's highest all-time-volume backed market — mirrors the creators tab's
// Top Market cell). Closest analog otherwise is the boosters tab.
// ---------------------------------------------------------------------------

interface BackersTabProps {
  initialSort?: BackerSort;
  onSortChange?: (sort: BackerSort) => void;
  anchorUserId?: string | undefined;
  pulseTick?: number | undefined;
  /** Time window forwarded to the leaderboard hook (`1d`/`1w`/`1m`/`1y`/`lifetime`). */
  timeWindow?: string;
}
function rankForActiveSort(entry: BackerEntry, sortBy: BackerSort): number {
  switch (sortBy) {
    case 'amount':
      return entry.amountRank;
    case 'backs':
      return entry.backsRank;
    case 'top_market':
      return entry.topMarketRank;
  }
}
function mapEntry(item: GetLeaderboardRow, sortBy: BackerSort): BackerEntry | null {
  const userField = (item.user ?? {}) as Record<string, unknown>;
  const providersRaw = Array.isArray(userField.connectedProviders) ? userField.connectedProviders as Array<Record<string, unknown>> : [];
  const totalBackedNetD6 = readFiniteNumericString(item.totalBackedNetD6);
  const numBacks = readFiniteNumber(item.numBacks);
  const topMarketPubkey = typeof item.topMarketPubkey === 'string' ? item.topMarketPubkey : '';
  const topMarketVolume = topMarketPubkey === '' ? '0' : readFiniteNumericString(item.topMarketVolume);
  const activeRank = readFiniteNumber(sortBy === 'amount' ? item.amountRank : sortBy === 'backs' ? item.backsRank : item.topMarketRank);
  const amountRank = item.amountRank === null ? activeRank : readFiniteNumber(item.amountRank);
  const backsRank = item.backsRank === null ? activeRank : readFiniteNumber(item.backsRank);
  const topMarketRank = item.topMarketRank === null ? activeRank : readFiniteNumber(item.topMarketRank);
  if (totalBackedNetD6 === null || numBacks === null || topMarketVolume === null || activeRank === null || amountRank === null || backsRank === null || topMarketRank === null) {
    return null;
  }
  return {
    user: {
      userId: typeof userField.userId === 'string' ? userField.userId : null,
      walletAddress: typeof userField.walletAddress === 'string' ? userField.walletAddress : '',
      displayName: typeof userField.displayName === 'string' ? userField.displayName : null,
      handle: typeof userField.handle === 'string' ? userField.handle : null,
      avatarUrl: typeof userField.avatarUrl === 'string' ? userField.avatarUrl : null,
      avatarNsfw: userField.avatarNsfw === true,
      avatarPreferences: parseAvatarPreferences(userField.avatarPreferences),
      connectedProviders: providersRaw.filter((p): p is {
        provider: 'x' | 'google' | 'apple';
      } & Record<string, unknown> => p.provider === 'x' || p.provider === 'google' || p.provider === 'apple').map(p => ({
        provider: p.provider,
        username: typeof p.username === 'string' ? p.username : '',
        displayName: typeof p.displayName === 'string' ? p.displayName : null
      }))
    },
    totalBackedNetD6,
    numBacks,
    // Flat top-market fields; null (no top market) → empty string / '0', which
    // the Top Market cell renders as a muted zero.
    topMarketPubkey,
    topMarketTitle: String(item.topMarketTitle ?? ''),
    topMarketVolume,
    amountRank,
    backsRank,
    topMarketRank
  };
}
export function BackersTab(t0) {
  const $ = _c(65);
  const {
    initialSort,
    onSortChange,
    anchorUserId,
    pulseTick,
    timeWindow
  } = t0;
  const tBackers = useTranslations("social.backers");
  const tLb = useTranslations("social.leaderboard");
  const {
    user
  } = useAuth();
  const router = useAppRouter();
  const prefetchUser = usePrefetchGetUser();
  const {
    formatBalance
  } = useFormattedCurrency();
  const userId = user?.id ?? null;
  const [sortBy, setSortBy] = useState(initialSort ?? "amount");
  const sortOrder = "desc" as const;
  const leaderboardWindow = timeWindow ?? "lifetime";
  let t1;
  if ($[0] !== onSortChange) {
    t1 = function handleSortChange(next) {
      setSortBy(next);
      if (onSortChange) {
        onSortChange(next);
      }
    };
    $[0] = onSortChange;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const handleSortChange = t1;
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = [...LB_BACKERS_FIELDS];
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== leaderboardWindow || $[4] !== sortBy) {
    t3 = {
      kind: "backers",
      sortBy,
      sortOrder: "desc",
      window: leaderboardWindow,
      fields: t2
    };
    $[3] = leaderboardWindow;
    $[4] = sortBy;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const leaderboardQuery = useGetLeaderboard(t3);
  const leaderboardStatus = leaderboardQuery.status;
  const leaderboardData = useLiveListData(leaderboardQuery.queryKey);
  const isResolved = leaderboardStatus === "success" && leaderboardQuery.isFetched && !leaderboardQuery.isPlaceholderData;
  let isInitialLoad;
  let rawEntries;
  let t4;
  if ($[6] !== anchorUserId || $[7] !== isResolved || $[8] !== leaderboardData || $[9] !== sortBy) {
    rawEntries = mapLeaderboardEntries(leaderboardData, isResolved, mapEntry, sortBy);
    isInitialLoad = !isResolved && rawEntries.length === 0;
    t4 = anchorUserId !== undefined && rawEntries.some(entry => entry.user.userId === anchorUserId);
    $[6] = anchorUserId;
    $[7] = isResolved;
    $[8] = leaderboardData;
    $[9] = sortBy;
    $[10] = isInitialLoad;
    $[11] = rawEntries;
    $[12] = t4;
  } else {
    isInitialLoad = $[10];
    rawEntries = $[11];
    t4 = $[12];
  }
  const isAnchorLoaded = t4;
  const t5 = leaderboardQuery.hasNextPage === true;
  const t6 = leaderboardQuery.isFetchingNextPage === true;
  let t7;
  if ($[13] !== anchorUserId || $[14] !== isAnchorLoaded || $[15] !== leaderboardQuery.fetchNextPage || $[16] !== pulseTick || $[17] !== t5 || $[18] !== t6) {
    t7 = {
      pulseTick,
      anchorUserId,
      isAnchorLoaded,
      hasNextPage: t5,
      isFetchingNextPage: t6,
      fetchNextPage: leaderboardQuery.fetchNextPage
    };
    $[13] = anchorUserId;
    $[14] = isAnchorLoaded;
    $[15] = leaderboardQuery.fetchNextPage;
    $[16] = pulseTick;
    $[17] = t5;
    $[18] = t6;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  const scrollTick = useAnchorScroll(t7);
  if (isInitialLoad) {
    let t8;
    if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
      t8 = <TabContent><Panel surface="solid" inheritBackground={true} className={SOCIAL_LEADERBOARD_LIST_PANEL_CLASS} data-testid="social-leaderboard-panel"><LeaderboardSkeleton columnCount={3} /></Panel></TabContent>;
      $[20] = t8;
    } else {
      t8 = $[20];
    }
    return t8;
  }
  if (rawEntries.length === 0) {
    let t8;
    if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
      t8 = <TabContent><Panel surface="solid" inheritBackground={true} className={SOCIAL_LEADERBOARD_LIST_PANEL_CLASS} data-testid="social-leaderboard-panel"><LeaderboardEmptyState tab="backers" /></Panel></TabContent>;
      $[21] = t8;
    } else {
      t8 = $[21];
    }
    return t8;
  }
  const t8 = leaderboardQuery.hasNextPage === true;
  const t9 = leaderboardQuery.isFetchingNextPage === true;
  const t10 = leaderboardQuery.fetchNextPage;
  const t11 = leaderboardQuery.isError;
  let t12;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <DataTable.RankColumn />;
    $[22] = t12;
  } else {
    t12 = $[22];
  }
  let t13;
  if ($[23] !== tLb) {
    t13 = tLb("trader");
    $[23] = tLb;
    $[24] = t13;
  } else {
    t13 = $[24];
  }
  let t14;
  if ($[25] !== t13) {
    t14 = <DataTable.IdentityColumn header={t13} />;
    $[25] = t13;
    $[26] = t14;
  } else {
    t14 = $[26];
  }
  let t15;
  if ($[27] !== tBackers) {
    t15 = tBackers("column_backed");
    $[27] = tBackers;
    $[28] = t15;
  } else {
    t15 = $[28];
  }
  let t16;
  if ($[29] !== t15) {
    t16 = <DataTable.SortableColumn sortKey="amount" header={t15} align="right" className="w-[28%] whitespace-nowrap" />;
    $[29] = t15;
    $[30] = t16;
  } else {
    t16 = $[30];
  }
  let t17;
  if ($[31] !== tBackers) {
    t17 = tBackers("column_backs");
    $[31] = tBackers;
    $[32] = t17;
  } else {
    t17 = $[32];
  }
  let t18;
  if ($[33] !== t17) {
    t18 = <DataTable.SortableColumn sortKey="backs" header={t17} align="right" className="w-[18%] whitespace-nowrap" />;
    $[33] = t17;
    $[34] = t18;
  } else {
    t18 = $[34];
  }
  let t19;
  if ($[35] !== tBackers) {
    t19 = tBackers("column_top_market");
    $[35] = tBackers;
    $[36] = t19;
  } else {
    t19 = $[36];
  }
  let t20;
  if ($[37] !== t19) {
    t20 = <DataTable.SortableColumn sortKey="top_market" header={t19} align="right" className="w-[22%] whitespace-nowrap" />;
    $[37] = t19;
    $[38] = t20;
  } else {
    t20 = $[38];
  }
  let t21;
  if ($[39] !== t14 || $[40] !== t16 || $[41] !== t18 || $[42] !== t20) {
    t21 = <DataTable.Header>{t12}{t14}{t16}{t18}{t20}</DataTable.Header>;
    $[39] = t14;
    $[40] = t16;
    $[41] = t18;
    $[42] = t20;
    $[43] = t21;
  } else {
    t21 = $[43];
  }
  let t22;
  if ($[44] !== formatBalance || $[45] !== prefetchUser || $[46] !== router || $[47] !== sortBy || $[48] !== tBackers || $[49] !== tLb || $[50] !== userId) {
    t22 = <DataTable.Body>{entry_0 => {
        const rank = rankForActiveSort(entry_0, sortBy);
        const entryUserId = entry_0.user.userId ?? "";
        const isUser = userId !== null && entry_0.user.userId === userId;
        const navigate = function navigate() {
          if (entryUserId.length === 0) {
            return;
          }
          router.push(`/portfolio/${entryUserId}`);
        };
        const prefetch = function prefetch() {
          if (entryUserId.length === 0) {
            return;
          }
          prefetchUser({
            identifier: entryUserId,
            fields: [...PORTFOLIO_USER_PROFILE_FIELDS]
          });
        };
        const topMarketContent = entry_0.topMarketPubkey.length > 0 ? <DataTable.ClickableCell href={`/markets/${entry_0.topMarketPubkey}`} ariaLabel={tBackers("top_market_arrow_aria")} align="right" hideOnMobile={true}>{formatBalance(Number(entry_0.topMarketVolume) / D6_SCALE, "compactTight")}</DataTable.ClickableCell> : <DataTable.NumericCell value={0} format="compact" tone="muted" hideOnMobile={true} />;
        return <DataTable.Row key={userIdentityKey(entry_0.user, `anonymous-rank-${rank}`)} isCurrentUser={isUser} onClick={navigate} onMouseEnter={prefetch} onFocus={prefetch} onPointerDown={prefetch} ariaLabel={tLb("aria_view_profile")}><DataTable.RankCell value={rank} isCurrentUser={isUser} /><DataTable.IdentityCell user={entry_0.user} showYouBadge={isUser} /><DataTable.NumericCell value={Number(entry_0.totalBackedNetD6)} format="compact" /><DataTable.NumericCell value={entry_0.numBacks} format="int" hideOnMobile={true} />{topMarketContent}</DataTable.Row>;
      }}</DataTable.Body>;
    $[44] = formatBalance;
    $[45] = prefetchUser;
    $[46] = router;
    $[47] = sortBy;
    $[48] = tBackers;
    $[49] = tLb;
    $[50] = userId;
    $[51] = t22;
  } else {
    t22 = $[51];
  }
  let t23;
  if ($[52] !== handleSortChange || $[53] !== rawEntries || $[54] !== scrollTick || $[55] !== sortBy || $[56] !== t21 || $[57] !== t22) {
    t23 = <DataTable panel="borderless" desktopRowChrome="feed" entries={rawEntries} sortBy={sortBy} sortOrder={sortOrder} onSortChange={handleSortChange} pulseTick={scrollTick}>{t21}{t22}</DataTable>;
    $[52] = handleSortChange;
    $[53] = rawEntries;
    $[54] = scrollTick;
    $[55] = sortBy;
    $[56] = t21;
    $[57] = t22;
    $[58] = t23;
  } else {
    t23 = $[58];
  }
  let t24;
  if ($[59] !== leaderboardQuery.fetchNextPage || $[60] !== leaderboardQuery.isError || $[61] !== t23 || $[62] !== t8 || $[63] !== t9) {
    t24 = <TabContent><Panel surface="solid" inheritBackground={true} className={SOCIAL_LEADERBOARD_LIST_PANEL_CLASS} data-testid="social-leaderboard-panel"><SocialFeedList hasNextPage={t8} isFetchingNextPage={t9} fetchNextPage={t10} isError={t11} data-testid="social-backers-scroll">{t23}</SocialFeedList></Panel></TabContent>;
    $[59] = leaderboardQuery.fetchNextPage;
    $[60] = leaderboardQuery.isError;
    $[61] = t23;
    $[62] = t8;
    $[63] = t9;
    $[64] = t24;
  } else {
    t24 = $[64];
  }
  return t24;
}